import React, { JSX, useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Filter,
  Bot,
  MessageSquare,
  FileText,
  Camera,
  BarChart3,
  Code,
  Mail,
  Shield,
  Music,
  Database,
  Play,
  Edit,
  Trash2,
  Star,
  Clock,
  Users,
  Activity,
  ArrowRight,
  Brain,
  Zap,
  LucideIcon,
  ExternalLink,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Loader,
  UserCog
} from 'lucide-react';
import AddApplicationModal from './AddApplicationModal';
import { applicationsMetaService } from '../../../../components/api/applicationsMetaService';
import PermissionsAssignmentModal from '../../common/Permissions/PermissionsAssignmentModal';

// Type definitions
interface Application {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  category: string;
  status: 'active' | 'beta' | 'development';
  agents: number;
  lastUsed: string;
  rating: number;
  color: string;
  tags: string[];
  endpoint: string;
  frontendUrl?: string;
  deploymentStatus?: 'deployed' | 'deploying' | 'failed' | 'not-deployed';
  version?: string;
  usage24h?: number;
  spacode?: string;
}

// Type definitions
export interface ApplicationFormData {
  name: string;           // Changed from 'title'
  description: string;
  category: string;
  icon: string;
  tags: string;
  endpoint: string;       // Added (was missing)
  agents: number;         // Added (was missing)
  color: string;          // Added (was missing)
  frontendEndpoint: string; // Changed from 'url'
  deploymentType: string; // Added (was missing)
  selectedAgents: string[];
  // New API fields (optional for backward compatibility)
  title: string;
  url: string;
  spacode: string;
}

const categories: string[] = ['All', 'Communication', 'Document Processing', 'Computer Vision', 'Analytics', 'Development', 'Productivity', 'Security', 'Audio Processing', 'General'];

// Application Card Component
interface ApplicationCardProps {
  app: Application;
  onEdit: (app: Application) => void;
  onDelete: (appId: string) => void;
  onLaunch: (app: Application) => void;
}

const ApplicationCard: React.FC<ApplicationCardProps> = ({ app, onEdit, onDelete, onLaunch }) => {
  const IconComponent = app.icon;
  const [showAssignmentModal, setAssignmentModal] = useState(false);

  const getStatusColor = (status: Application['status']): string => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-400/10 border-green-400/20';
      case 'beta': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
      case 'development': return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
      default: return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
    }
  };

  const getDeploymentStatusIcon = (status?: Application['deploymentStatus']): JSX.Element => {
    switch (status) {
      case 'deployed': return <CheckCircle size={12} className="text-green-400" />;
      case 'deploying': return <Loader size={12} className="text-yellow-400 animate-spin" />;
      case 'failed': return <AlertCircle size={12} className="text-red-400" />;
      default: return <AlertCircle size={12} className="text-gray-400" />;
    }
  };

  const renderStars = (rating: number): JSX.Element[] => {
    const stars: JSX.Element[] = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} size={10} className="text-yellow-400 fill-yellow-400" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<Star key={i} size={10} className="text-yellow-400 fill-yellow-400 opacity-50" />);
      } else {
        stars.push(<Star key={i} size={10} className="text-gray-600" />);
      }
    }
    return stars;
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5 hover:border-indigo-500/30 transition-all group">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className={`p-2.5 rounded-lg bg-opacity-15 border border-opacity-20`}>
            <IconComponent size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base leading-tight">{app.name}</h3>
            <p className="text-gray-400 text-xs mt-0.5">{app.category}</p>
            {app.version && (
              <p className="text-gray-500 text-xs">{app.version}</p>
            )}
          </div>
        </div>
        <div className={`px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(app.status)}`}>
          {app.status}
        </div>
      </div>

      {/* Description */}
      <p className="text-gray-300 text-xs leading-relaxed mb-3">{app.description}</p>

      {/* Metrics Row */}
      <div className="flex items-center justify-between mb-3 text-xs">
        <div className="flex items-center space-x-4">
          <div className="flex items-center text-gray-400">
            <Bot size={12} className="mr-1" />
            <span>{app.agents}</span>
          </div>
          <div className="flex items-center text-gray-400">
            <Clock size={12} className="mr-1" />
            <span>{app.lastUsed}</span>
          </div>
          {app.usage24h !== undefined && (
            <div className="flex items-center text-gray-400">
              <TrendingUp size={12} className="mr-1" />
              <span>{app.usage24h}</span>
            </div>
          )}
        </div>
      </div>

      {/* Deployment Status */}
      {app.deploymentStatus && (
        <div className="flex items-center justify-between mb-3 text-xs">
          <div className="flex items-center space-x-2">
            {getDeploymentStatusIcon(app.deploymentStatus)}
            <span className="text-gray-400 capitalize">{app.deploymentStatus}</span>
          </div>
          {app.frontendUrl && (
            <a
              href={`/app/${app.frontendUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              <ExternalLink size={12} className="mr-1" />
              <span>Live</span>
            </a>
          )}
        </div>
      )}

      {/* Tags */}
      <div className="flex flex-wrap gap-1 mb-3">
        {app.tags.slice(0, 3).map((tag, index) => (
          <span
            key={index}
            className="px-2 py-0.5 bg-neutral-800 text-gray-300 rounded-full text-xs"
          >
            {tag}
          </span>
        ))}
        {app.tags.length > 3 && (
          <span className="px-2 py-0.5 bg-neutral-800 text-gray-400 rounded-full text-xs">
            +{app.tags.length - 3}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-neutral-800">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onLaunch(app)}
            className="flex items-center px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={app.status === 'development'}
          >
            <Play size={12} className="mr-1" />
            Launch
          </button>
          <button
            className="text-indigo-400 hover:text-indigo-300 transition-colors"
            title="Manage Permissions & Access"
            onClick={() => setAssignmentModal(true)}
          >
            <UserCog className="w-4 h-4" />
          </button>

          <button
            onClick={() => onEdit(app)}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-neutral-800 rounded-md transition-colors"
            title="Edit Application"
          >
            <Edit size={12} />
          </button>

          <button
            onClick={() => onDelete(app.id)}
            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-neutral-800 rounded-md transition-colors"
            title="Delete Application"
          >
            <Trash2 size={12} />
          </button>
        </div>
        <div className="text-xs text-gray-500 font-mono">
          {app.endpoint}
        </div>
      </div>

      {showAssignmentModal && (
        <PermissionsAssignmentModal
          onClose={() => setAssignmentModal(false)}
          onSave={() => { }}
          entityType="chat-agent"
          entityName="Chat Agents"
        // Optional: Pass initial permissions
        // initialPermissions={{
        //     departments: [],
        //     users: [],
        //     roles: [],
        //     exclusions: []
        // }}
        />
      )}
    </div>
  );
};

// Main Application Listing Component
const ApplicationListing: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('name');
  const [loading, setLoading] = useState<boolean>(true);
  const [editingApp, setEditingApp] = useState<Application | null>(null);


  // Tenant ID - In a real app, this would come from authentication/context
  const tenantId = localStorage.getItem('tenant_id') ?? '';

  // Icon mapping for dynamic icon resolution
  const iconMap: Record<string, LucideIcon> = {
    Bot,
    MessageSquare,
    FileText,
    Camera,
    BarChart3,
    Code,
    Mail,
    Shield,
    Music,
    Database,
    Zap,
    Brain,
    autio: Music, // Map your API's "autio" to Music icon
    audio: Music,
    chat: MessageSquare,
    document: FileText,
    vision: Camera,
    analytics: BarChart3,
    security: Shield,
    email: Mail,
    code: Code
  };

  // Transform API response to Application format
  const transformApiApplication = (apiApp: any): Application => {
    return {
      id: apiApp.id,
      name: apiApp.title,
      description: apiApp.description,
      icon: iconMap[apiApp.icon] || Bot,
      category: apiApp.category,
      status: apiApp.status === 1 ? 'active' : apiApp.status === 2 ? 'beta' : 'development',
      agents: parseInt(apiApp.mappedAgentsCount) || 0,
      lastUsed: 'Never',
      rating: 0,
      color: 'bg-blue-500',
      tags: typeof apiApp.tags === 'string' ? apiApp.tags.split(',').map((tag: string) => tag.trim()).filter(tag => tag) : [],
      endpoint: '/api/v1/' + (apiApp.title || '').toLowerCase().replace(/\s+/g, '-'),
      frontendUrl: apiApp.url,
      deploymentStatus: 'deployed',
      version: apiApp.version,
      usage24h: 0,
      spacode: apiApp.spacode
    };
  };


  // Load applications from API
  const loadApplications = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await applicationsMetaService.getAllByTenant(tenantId);

      // Handle direct array response or wrapped response
      let data = response;
      if (response && typeof response === 'object' && 'data' in response) {
        data = response.data;
      }

      if (Array.isArray(data)) {
        const transformedApps = data.map(transformApiApplication);
        setApplications(transformedApps);
      } else {
        console.error('Expected array but got:', data);
        setApplications([]);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadApplications();
  }, [tenantId]);

  // Delete application
  const handleDeleteApplication = async (appId: string): Promise<void> => {
    try {
      const response = await applicationsMetaService.delete(appId);

      // Handle successful deletion
      if (response && (response.success || response.success !== false)) {
        setApplications(prev => prev.filter(app => app.id !== appId));
      } else {
        console.error('Delete failed:', response);
      }
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  // Edit application (placeholder - would need API endpoint)
  const handleEditApplication = async (app: Application): Promise<void> => {
    try {
      // Fetch full application data
      const response = await applicationsMetaService.edit(app.id);
      // Transform API response to Application format
      const fullAppData = transformApiApplication(response);
      console.log('Editing application:', fullAppData);
      setEditingApp(fullAppData);
      setShowAddModal(true);
    } catch (error) {
      console.error('Error fetching application details:', error);
    }
  };

  // Launch application
  const handleLaunchApplication = (app: Application): void => {
    console.log('Launch application:', app);
    if (app.frontendUrl) {
      window.open(app.frontendUrl, '_blank');
    }
  };

  const handleAddApplication = async (formData: ApplicationFormData): Promise<void> => {
    const IconComponent = iconMap[formData.icon] || Bot;

    try {
      if (editingApp) {
        // UPDATE mode 
        const updatePayload = {
          id: editingApp.id,
          title: formData.title,
          description: formData.description,
          tags: formData.tags,
          status: 1,
          icon: formData.icon,
          url: formData.url,
          spacode: formData.spacode,
          version: editingApp.version || "1.0.0",
          createdBy: "current-user",
          tenantId: localStorage.getItem('tenant_id'),
          mappedAgentsCount: formData.selectedAgents.length.toString(),
          category: formData.category
        };

        await applicationsMetaService.update(updatePayload);

        // Update local state
        setApplications(prev => prev.map(app =>
          app.id === editingApp.id
            ? {
              ...app,
              name: formData.title,
              description: formData.description,
              category: formData.category,
              icon: IconComponent,
              tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
              frontendUrl: formData.url,
              agents: formData.selectedAgents.length
            }
            : app
        ));

        setEditingApp(null);
      } else {
        // CREATE mode (existing code)
        const newApp: Application = {
          ...formData,
          id: Date.now().toString(),
          icon: IconComponent,
          lastUsed: 'Never',
          rating: 0,
          status: 'development',
          tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
          frontendUrl: formData.frontendEndpoint || undefined,
          deploymentStatus: 'not-deployed',
          version: 'v0.1.0',
          usage24h: 0
        };

        setApplications([...applications, newApp]);
      }
    } catch (error) {
      console.error('Error saving application:', error);
    }
  };

  // Filter applications
  const filteredApplications = applications.filter((app) => {
    const matchesSearch =
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = selectedCategory === 'All' || app.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Sort applications
  const sortedApplications = [...filteredApplications].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'usage':
        return (b.usage24h || 0) - (a.usage24h || 0);
      case 'rating':
        return b.rating - a.rating;
      case 'lastUsed':
        if (a.lastUsed === 'Never') return 1;
        if (b.lastUsed === 'Never') return -1;
        return 0;
      default:
        return 0;
    }
  });

  const [showAssignmentModal, setAssignmentModal] = useState(false);
  // Calculate stats
  const activeApps = applications.filter(app => app.status === 'active').length;
  const totalAgents = applications.reduce((sum, app) => sum + app.agents, 0);
  const avgRating = applications.filter(app => app.rating > 0).length > 0
    ? applications.reduce((sum, app) => sum + app.rating, 0) / applications.filter(app => app.rating > 0).length
    : 0;
  const totalUsage = applications.reduce((sum, app) => sum + (app.usage24h || 0), 0);

  return (
    <div>
      {/* Header */}
      <div className="border-b border-neutral-800 bg-neutral-900/50">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center">
                <Brain className="mr-3 text-indigo-400" size={28} />
                Applications
              </h1>
              <p className="text-gray-400 text-sm mt-1">Enterprise AI application management platform</p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-lg text-sm"
            >
              <Plus size={16} className="mr-2" />
              Create Application
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
            <div className="bg-neutral-800/50 rounded-lg p-3">
              <div className="text-white font-semibold">{applications.length}</div>
              <div className="text-gray-400 text-xs">Total Apps</div>
            </div>
            <div className="bg-neutral-800/50 rounded-lg p-3">
              <div className="text-green-400 font-semibold">{activeApps}</div>
              <div className="text-gray-400 text-xs">Active</div>
            </div>
            <div className="bg-neutral-800/50 rounded-lg p-3">
              <div className="text-indigo-400 font-semibold">{totalAgents}</div>
              <div className="text-gray-400 text-xs">Total Agents</div>
            </div>
            <div className="bg-neutral-800/50 rounded-lg p-3">
              <div className="text-yellow-400 font-semibold">{totalUsage.toLocaleString()}</div>
              <div className="text-gray-400 text-xs">24h Usage</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-5">
        {/* Filters and Search */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search applications, descriptions, or tags..."
              className="w-full bg-neutral-800 border border-neutral-700 text-white pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center space-x-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-neutral-800 border border-neutral-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center space-x-2">
            <Activity size={16} className="text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-neutral-800 border border-neutral-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            >
              <option value="name">Sort by Name</option>
              <option value="rating">Sort by Rating</option>
              <option value="usage">Sort by Usage</option>
              <option value="lastUsed">Sort by Last Used</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <Loader size={40} className="mx-auto mb-4 text-indigo-400 animate-spin" />
            <h3 className="text-lg font-semibold text-gray-400 mb-2">Loading applications...</h3>
            <p className="text-gray-500 text-sm">Please wait while we fetch your applications</p>
          </div>
        )}

        {/* Applications Grid */}
        {!loading && (
          <>
            {sortedApplications.length === 0 ? (
              <div className="text-center py-12">
                <Bot size={40} className="text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-400 mb-2">No applications found</h3>
                <p className="text-gray-500 text-sm">Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {sortedApplications.map((app) => (
                  <ApplicationCard
                    key={app.id}
                    app={app}
                    onEdit={handleEditApplication}
                    onDelete={handleDeleteApplication}
                    onLaunch={handleLaunchApplication}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Application Modal */}
      <AddApplicationModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingApp(null);
        }}
        onSave={handleAddApplication}
        editMode={!!editingApp}
        initialData={editingApp}
      />

    </div>
  );
};

export default ApplicationListing;